import { useState } from "react";
import { useDataProvider, useGetList, useNotify, useRedirect } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CrmDataProvider } from "../providers/types";

/** What the loser carries, so the confirmation can say what will move. */
export interface MergeSummary {
  identifiers: number;
  types: number;
  affiliations: number;
  links: number;
  deals: number;
}

interface GraphMergeButtonProps {
  loserId: string;
  firstName: string;
  lastName: string;
  summary: MergeSummary;
}

const MERGE_ERRORS: Record<string, string> = {
  already_merged: "One of these contacts has already been merged.",
  not_found: "One of these contacts no longer exists.",
};

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

interface MergeCandidate {
  id: string;
  first_name: string;
  last_name: string;
  primary_type?: string | null;
}

export function GraphMergeButton({
  loserId,
  firstName,
  lastName,
  summary,
}: GraphMergeButtonProps) {
  const [winnerId, setWinnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data } = useGetList<MergeCandidate>("contacts", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "last_name", order: "ASC" },
    filter: { first_name: firstName, last_name: lastName },
  });
  const candidates = (data ?? []).filter((row) => row.id !== loserId);

  if (candidates.length === 0) return null;

  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="w-full text-sm">
        Merge into
        <Select value={winnerId} onValueChange={setWinnerId}>
          <SelectTrigger
            className="mt-1 w-full max-w-80"
            aria-label="Merge into"
          >
            <SelectValue placeholder="Choose the matching contact" />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((row) => (
              <SelectItem key={row.id} value={row.id}>
                {row.first_name} {row.last_name}
                {row.primary_type ? ` · ${row.primary_type}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <Button
        variant="outline"
        disabled={busy || !winnerId}
        onClick={() => setConfirming(true)}
      >
        Merge {firstName} {lastName}
      </Button>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Merge {firstName} {lastName} into the selected contact?
            </DialogTitle>
            <DialogDescription>
              This moves {plural(summary.identifiers, "identifier")},{" "}
              {plural(summary.types, "contact type")},{" "}
              {plural(summary.affiliations, "affiliation")},{" "}
              {plural(summary.deals, "deal role")} and{" "}
              {plural(summary.links, "relationship")} to the contact you chose.
              Anything the chosen contact already has is kept once. This contact
              stays only as a pointer to the merged one, and the merge cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={busy}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              disabled={busy}
              onClick={async () => {
                try {
                  setBusy(true);
                  await dataProvider.mergeContacts?.(loserId, winnerId);
                  // The redirect below stays inside this same contact-show
                  // tree, so the candidate list above never remounts and never
                  // refetches. ra-core keys queries as [resource, method,
                  // params], so the bare name also refreshes the winner's show.
                  await queryClient.invalidateQueries({
                    queryKey: ["contacts"],
                  });
                  notify("Contacts merged", { type: "success" });
                  setConfirming(false);
                  redirect("show", "contacts", winnerId);
                } catch (error: unknown) {
                  const code = error instanceof Error ? error.message : "";
                  notify(MERGE_ERRORS[code] ?? "Merge failed", {
                    type: "error",
                  });
                } finally {
                  setBusy(false);
                }
              }}
            >
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
